import React, { useCallback, useEffect } from "react";

import { toast } from "@heroui/react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { confirmPasswordReset, verifyPasswordResetCode } from "firebase/auth";

import { AppRoutes } from "@domain/routing/routeConstants";
import { AuthLayout } from "./AuthLayout";
import { NewPasswordForm } from "../profile/ProfilePassword/form";
import { auth } from "@domain/api/firebase/firebase";
import { logger } from "@domain/infrastructure/logger";

const NEW_PASSWORD_TITLE = <FormattedMessage id="app.new_password.caption" />;

export const NewPasswordView = () => {
    const navigate = useNavigate();
    const { formatMessage } = useIntl();
    const [query] = useSearchParams();

    const checkCode = async (code: string) => {
        if (!code) {
            toast.danger(formatMessage({ id: "app.global.error" }));
            navigate(AppRoutes.home);
            return;
        }
        try {
            await verifyPasswordResetCode(auth, code);
        } catch {
            toast.danger(formatMessage({ id: "app.global.error" }));
            navigate(AppRoutes.home);
        }
    };

    useEffect(() => {
        checkCode(query.get("oobCode"));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSubmit = useCallback(
        async (newPassword: string): Promise<{ redirect: boolean } | void> => {
            try {
                await confirmPasswordReset(auth, query.get("oobCode"), newPassword);
                toast(formatMessage({ id: "app.new_password.confirmation" }));
                navigate(AppRoutes.authLogin);
                return { redirect: true };
            } catch (error) {
                logger.error(error);
                toast.danger(formatMessage({ id: "app.global.error" }));
            }
        },
        [query, navigate, formatMessage],
    );

    return (
        <AuthLayout title={NEW_PASSWORD_TITLE}>
            <NewPasswordForm onSubmit={handleSubmit} />
            <div className="flex justify-between">
                <div>
                    <Link to={AppRoutes.authLogin}>
                        <FormattedMessage id="app.global.login_now" />
                    </Link>
                </div>
                <div>
                    <Link to={AppRoutes.authRegister}>
                        <FormattedMessage id="app.global.register_now" />
                    </Link>
                </div>
            </div>
        </AuthLayout>
    );
};
